import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Skeleton,
  Chip,
  Box,
} from '@mui/material';
import { Encounter } from '../../types/types';
import { useAccessToken, useSubjectLanguage } from '@features/user';
import APIService from '../../services/APIService';
import { capitalize, formatDateString } from '../../utils/utils';
import { useNavigate } from 'react-router-dom';
import FullScreenLoader from '../FullScreenLoader';
import BaseBlock from './Blocks/BaseBlock';
import { EncounterStatus } from '@features/encounters/constants';

interface PatientEncountersBlockProps {
  patientId: string;
}

const PatientEncountersBlock: React.FC<PatientEncountersBlockProps> = ({ patientId }) => {
  const { getAccessToken } = useAccessToken();
  const subjectLanguage = useSubjectLanguage();
  const navigate = useNavigate();

  const [encounters, setEncounters] = useState<Encounter[]>([]);
  const [loading, setLoading] = useState(true);
  const [noteLoading, setNoteLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalRows, setTotalRows] = useState(0);

  const fetchEncounters = useCallback(
    async (page: number, rowsPerPage: number) => {
      setLoading(true);
      try {
        const accessToken = await getAccessToken();
        const response = await APIService.makeAPIGetRequest({
          requestString: `/v2/provider/encounters?patient_id=${patientId}&page=${
            page + 1
          }&per_page=${rowsPerPage}`,
          accessToken,
        });

        if (response.ok) {
          const data = await response.value;
          setEncounters(data.items);
          setTotalRows(data.total);
        } else {
          throw new Error('Failed to fetch encounters');
        }
      } catch (error) {
        console.error('Error fetching encounters:', error);
      } finally {
        setLoading(false);
      }
    },
    [getAccessToken, patientId],
  );

  useEffect(() => {
    fetchEncounters(page, rowsPerPage);
  }, [page, rowsPerPage]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (encounter: Encounter) => {
    setNoteLoading(true);

    // Navigate to note page if encounter is completed, otherwise go to encounter page
    if (encounter.status === EncounterStatus.COMPLETED && encounter.note_id) {
      navigate(`/note/${encounter.note_id}`);
    } else {
      navigate(`/encounters/${encounter.encounter_id}`);
    }
  };

  return (
    <>
      <BaseBlock title={`${capitalize(subjectLanguage)} Encounters`}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date/Time</TableCell>
              <TableCell>Encounter Type</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? Array.from(new Array(rowsPerPage)).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Skeleton animation="wave" />
                    </TableCell>
                    <TableCell>
                      <Skeleton animation="wave" />
                    </TableCell>
                  </TableRow>
                ))
              : encounters.map(encounter => (
                  <TableRow
                    key={encounter.encounter_id}
                    hover
                    onClick={() => handleRowClick(encounter)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      {formatDateString(
                        (encounter.start_time || encounter.appointment?.start_time)! ||
                          encounter.created_at,
                      )}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {encounter.encounter_type}
                        {encounter.status === 'pending' && (
                          <Chip
                            label="Pending"
                            size="small"
                            sx={{
                              backgroundColor: '#ff9800',
                              color: 'white',
                              fontSize: '0.75rem',
                            }}
                          />
                        )}
                        {encounter.status === 'processing' && (
                          <Chip
                            label="Processing"
                            size="small"
                            sx={{
                              backgroundColor: '#2196f3',
                              color: 'white',
                              fontSize: '0.75rem',
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalRows}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 20]}
        />
      </BaseBlock>
      {noteLoading && <FullScreenLoader />}
    </>
  );
};

export default React.memo(PatientEncountersBlock);
